import React from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

const ServicePill = ({
  service,
  index,
  isActive,
  isMobile,
  isVisible,
  reducedMotion,
  onActivate,
  onDeactivate,
}) => {
  const floatY = reducedMotion || isMobile ? 0 : service.float;
  const { position } = service;

  return (
    <Box
      component={motion.div}
      className="service-pill-wrap"
      sx={{
        position: { xs: "relative", md: "absolute" },
        top: { md: position.top },
        left: { md: position.left },
        right: { md: position.right },
        width: { xs: "100%", md: position.width || 216 },
        zIndex: isActive ? 5 : 3,
      }}
      initial={{ opacity: 0, y: 16, scale: 0.94 }}
      animate={isVisible ? { opacity: 1, y: 0, scale: 1 } : undefined}
      transition={{ duration: 0.6, delay: 0.2 + index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <Box
        component={motion.button}
        type="button"
        aria-label={`${service.title}: ${service.description}`}
        onMouseEnter={onActivate}
        onMouseLeave={onDeactivate}
        onFocus={onActivate}
        onBlur={onDeactivate}
        whileHover={reducedMotion ? undefined : { scale: 1.04 }}
        animate={{ y: floatY ? [0, -floatY, 0, floatY * 0.5, 0] : 0 }}
        transition={{
          y: {
            duration: service.duration,
            delay: service.delay,
            repeat: Infinity,
            ease: "easeInOut",
          },
          scale: { duration: 0.2 },
        }}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          width: "100%",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.3,
          borderRadius: "16px",
          background: isActive
            ? "linear-gradient(135deg, #00D5C8, #03b1a4)"
            : "linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.035))",
          border: isActive
            ? "1px solid rgba(0,213,200,0.9)"
            : "1px solid rgba(0,213,200,0.22)",
          boxShadow: isActive
            ? "0 18px 50px rgba(0,213,200,0.4), inset 0 1px 12px rgba(255,255,255,0.2)"
            : "0 16px 40px rgba(0,0,0,0.32), inset 0 1px 10px rgba(255,255,255,0.06)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          transition:
            "background 220ms ease, border-color 220ms ease, box-shadow 220ms ease",
          "&:focus-visible": {
            outline: "2px solid rgba(212,176,106,0.95)",
            outlineOffset: "4px",
          },
        }}
      >
        <Box
          sx={{
            flex: "0 0 auto",
            width: 42,
            height: 42,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 4px 12px rgba(0,0,0,0.16)",
          }}
        >
          <Box
            component="img"
            src={service.icon}
            alt=""
            aria-hidden="true"
            sx={{ width: 24, height: 24, objectFit: "contain" }}
          />
        </Box>
        <Box sx={{ minWidth: 0, textAlign: "start" }}>
          <Typography
            sx={{
              color: isActive ? "#06231F" : "#FFFFFF",
              fontSize: { xs: "0.95rem", md: "0.98rem" },
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: 0,
              transition: "color 220ms ease",
            }}
          >
            {service.title}
          </Typography>
          <Box
            component={motion.div}
            initial={false}
            animate={{
              height: isActive ? "auto" : 0,
              opacity: isActive ? 1 : 0,
              marginTop: isActive ? 4 : 0,
            }}
            transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
            sx={{ overflow: "hidden" }}
          >
            <Typography
              sx={{
                color: "rgba(6,35,31,0.82)",
                fontSize: { xs: "0.76rem", md: "0.78rem" },
                fontWeight: 500,
                lineHeight: 1.4,
                letterSpacing: 0,
              }}
            >
              {service.description}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ServicePill;
